import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { getChargements, uploadChargement, deleteChargement, getParametrage, getProjets } from '../services/api';
import { FiUpload, FiTrash2, FiFile, FiCheckCircle, FiAlertCircle, FiX, FiPlus, FiEye, FiArrowLeft, FiSend } from 'react-icons/fi';

const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';

interface PreviewData {
  headers: string[];
  colMap: Record<string, number>;
  rows: any[][];
  totalRows: number;
  totalMontant: number;
  sampleRows: any[][];
  invalidRows: number;
}

const TRIMESTRE_MAP: Record<string, number> = {
  '1': 4, '2': 7, '3': 10, '4': 1,
  'trimestre 1': 4, 'trimestre 2': 7, 'trimestre 3': 10, 'trimestre 4': 1,
  't1': 4, 't2': 7, 't3': 10, 't4': 1
};

const Chargements = () => {
  const [chargements, setChargements] = useState<any[]>([]);
  const [typepaiements, setTypepaiements] = useState<any[]>([]);
  const [projets, setProjets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);

  // Form visibility & steps
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState<'form' | 'recap'>('form');
  const [preview, setPreview] = useState<PreviewData | null>(null);

  // Form fields
  const [libelle, setLibelle] = useState('');
  const [description, setDescription] = useState('');
  const [typepaiementId, setTypepaiementId] = useState('');
  const [projetId, setProjetId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const loadData = () => {
    getChargements().then(r => setChargements(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadData();
    getParametrage('typepaiements').then(r => setTypepaiements(r.data)).catch(() => {});
    getProjets().then(r => setProjets(r.data)).catch(() => {});
  }, []);

  const resetForm = () => {
    setLibelle('');
    setDescription('');
    setTypepaiementId('');
    setProjetId('');
    setFile(null);
    setResult(null);
    setPreview(null);
    setStep('form');
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  // Client-side Excel parsing for preview
  const parseFileForPreview = (selectedFile: File): Promise<PreviewData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

          if (rows.length < 2) {
            reject(new Error('Le fichier est vide ou ne contient que les en-têtes'));
            return;
          }

          const headers = rows[0].map((h: any) => String(h || '').trim());
          const headersLower = headers.map((h: string) => h.toLowerCase());

          // Detect columns (same logic as backend)
          const colMap: Record<string, number> = {};
          const knownFields = ['matricule', 'prenom', 'nom', 'montant', 'libelle', 'annee', 'dateversement', 'reference'];
          headersLower.forEach((h: string, i: number) => {
            const normalized = h.replace(/[éèê]/g, 'e').replace(/[àâ]/g, 'a');
            if (knownFields.includes(normalized)) colMap[normalized] = i;
            else if (h.includes('matricule') || h.includes('mat')) colMap['matricule'] = i;
            else if (h.includes('prenom') || h.includes('prénom')) colMap['prenom'] = i;
            else if (h === 'nom') colMap['nom'] = i;
            else if (h.includes('montant') || h.includes('amount')) colMap['montant'] = i;
            else if (h.includes('libelle') || h.includes('libellé')) colMap['libelle'] = i;
            else if (h.includes('annee') || h.includes('année') || h.includes('year')) colMap['annee'] = i;
            else if (h.includes('date')) colMap['dateversement'] = i;
            else if (h.includes('ref')) colMap['reference'] = i;
          });

          if (colMap['matricule'] === undefined || colMap['montant'] === undefined) {
            reject(new Error(`Colonnes requises manquantes ("Matricule" et "montant"). Colonnes détectées : ${headers.join(', ')}`));
            return;
          }

          // Compute stats
          let totalMontant = 0;
          let invalidRows = 0;
          const dataRows = rows.slice(1).filter((row: any[]) => row && row.length > 0);

          const validRows: any[][] = [];
          for (const row of dataRows) {
            const matricule = colMap['matricule'] !== undefined ? String(row[colMap['matricule']] || '').trim() : '';
            const prenom = colMap['prenom'] !== undefined ? String(row[colMap['prenom']] || '').trim() : '';
            const nom = colMap['nom'] !== undefined ? String(row[colMap['nom']] || '').trim() : '';
            const montant = colMap['montant'] !== undefined ? parseInt(row[colMap['montant']]) || 0 : 0;

            if (!matricule && !prenom && !nom) continue; // skip empty rows
            if (montant <= 0) {
              invalidRows++;
              continue;
            }
            totalMontant += montant;
            validRows.push(row);
          }

          resolve({
            headers,
            colMap,
            rows: validRows,
            totalRows: validRows.length,
            totalMontant,
            sampleRows: validRows.slice(0, 8),
            invalidRows
          });
        } catch (err: any) {
          reject(new Error('Impossible de lire le fichier : ' + (err.message || 'format invalide')));
        }
      };
      reader.onerror = () => reject(new Error('Erreur de lecture du fichier'));
      reader.readAsArrayBuffer(selectedFile);
    });
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return setError('Veuillez sélectionner un fichier');

    setError('');
    setLoading(true);
    try {
      const previewData = await parseFileForPreview(file);
      setPreview(previewData);
      setStep('recap');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'analyse du fichier');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!file) return;

    setLoading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('fichier', file);
    formData.append('libelle', libelle || file.name);
    formData.append('description', description);
    if (typepaiementId) formData.append('typepaiement_id', typepaiementId);
    if (projetId) formData.append('projet_id', projetId);

    try {
      const r = await uploadChargement(formData);
      setResult(r.data);
      setSuccess(`${r.data.lignes_importees} versements importés avec succès`);
      loadData();
      resetForm();
      setShowForm(false);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      const errData = err.response?.data;
      setError(errData?.error || 'Erreur lors du chargement');
      if (errData?.colonnes_detectees) {
        setResult({ colonnes_detectees: errData.colonnes_detectees });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, libelle: string, nb: number) => {
    if (!window.confirm(`Supprimer le chargement "${libelle}" et ses ${nb} versements associés ?`)) return;
    try {
      const r = await deleteChargement(id);
      setSuccess(r.data.message);
      loadData();
      setTimeout(() => setSuccess(''), 4000);
    } catch {
      setError('Erreur lors de la suppression');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Render column name from colMap for preview
  const getPreviewValue = (row: any[], field: string) => {
    if (!preview || preview.colMap[field] === undefined) return '-';
    const val = row[preview.colMap[field]];
    if (val === null || val === undefined || val === '') return '-';
    return String(val);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Chargement de versements</h1>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => { setShowForm(true); resetForm(); }}>
            <FiPlus /> Nouveau chargement
          </button>
        )}
      </div>

      {success && <div className="alert alert-success"><FiCheckCircle /> {success}</div>}
      {error && <div className="alert alert-error"><FiAlertCircle /> {error}</div>}

      {/* Upload result after import */}
      {result && result.lignes_importees !== undefined && !showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-body">
            <div className="upload-result">
              <div className="upload-result-stats">
                <div className="upload-stat success"><FiCheckCircle /> <strong>{result.lignes_importees}</strong> lignes importées</div>
                {result.erreurs > 0 && <div className="upload-stat error"><FiAlertCircle /> <strong>{result.erreurs}</strong> erreurs</div>}
                <div className="upload-stat info">Total: <strong>{formatMontant(result.montant_total)}</strong></div>
              </div>
              {result.error_details && result.error_details.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#c62828' }}>Voir les erreurs ({result.error_details.length})</summary>
                  <ul style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>
                    {result.error_details.map((e: string, i: number) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload card - hidden until "Nouveau chargement" clicked */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              {step === 'form' ? (
                <><FiUpload /> Importer un fichier de versements</>
              ) : (
                <><FiEye /> Récapitulatif avant import</>
              )}
            </span>
            <button className="btn-icon" onClick={closeForm} title="Fermer"><FiX /></button>
          </div>
          <div className="card-body">
            {/* Step 1: Form */}
            {step === 'form' && (
              <form onSubmit={handlePreview}>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Fichier Excel (.xlsx, .xls, .csv) *</label>
                    <div className="file-upload-zone" onClick={() => document.getElementById('file-input')?.click()}>
                      <input id="file-input" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                        onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
                      {file ? (
                        <div className="file-upload-selected">
                          <FiFile style={{ fontSize: '1.5rem', color: '#1565C0' }} />
                          <div>
                            <strong>{file.name}</strong>
                            <small style={{ display: 'block', color: '#888' }}>{(file.size / 1024).toFixed(1)} Ko</small>
                          </div>
                          <button type="button" className="btn-icon" onClick={e => { e.stopPropagation(); setFile(null); }}><FiX /></button>
                        </div>
                      ) : (
                        <div className="file-upload-placeholder">
                          <FiUpload style={{ fontSize: '2rem', color: '#999' }} />
                          <span>Cliquer pour sélectionner un fichier</span>
                          <small>Format : Matricule, Prenom, Nom, montant, Libelle, Annee</small>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Libellé</label>
                    <input className="form-input" value={libelle} onChange={e => setLibelle(e.target.value)} placeholder="Ex: Prélèvement FC T3 2025" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type de paiement</label>
                    <select className="form-input" value={typepaiementId} onChange={e => setTypepaiementId(e.target.value)}>
                      <option value="">--</option>
                      {typepaiements.map((t: any) => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Projet</label>
                    <select className="form-input" value={projetId} onChange={e => setProjetId(e.target.value)}>
                      <option value="">--</option>
                      {projets.map((pr: any) => <option key={pr.id} value={pr.id}>{pr.libelle || pr.nom}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description optionnelle" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button type="submit" className="btn btn-primary" disabled={loading || !file}>
                    {loading ? 'Analyse en cours...' : <><FiEye /> Aperçu</>}
                  </button>
                  <button type="button" className="btn btn-outline" onClick={closeForm}>Annuler</button>
                </div>
              </form>
            )}

            {/* Step 2: Recap / Preview */}
            {step === 'recap' && preview && (
              <div>
                {/* Summary stats */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
                  <div style={{ background: '#e8f5e9', borderRadius: 10, padding: '14px 22px', flex: '1 1 180px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#2e7d32' }}>{preview.totalRows.toLocaleString('fr-FR')}</div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>Lignes valides</div>
                  </div>
                  <div style={{ background: '#e3f2fd', borderRadius: 10, padding: '14px 22px', flex: '1 1 200px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1565C0' }}>{formatMontant(preview.totalMontant)}</div>
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>Montant total</div>
                  </div>
                  {preview.invalidRows > 0 && (
                    <div style={{ background: '#fff3e0', borderRadius: 10, padding: '14px 22px', flex: '1 1 180px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#e65100' }}>{preview.invalidRows}</div>
                      <div style={{ fontSize: '0.85rem', color: '#555' }}>Lignes ignorées (montant invalide)</div>
                    </div>
                  )}
                </div>

                {/* Form recap */}
                <div style={{ background: '#f5f5f5', borderRadius: 10, padding: '14px 18px', marginBottom: 16, fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
                    <span><strong>Fichier :</strong> {file?.name}</span>
                    {libelle && <span><strong>Libellé :</strong> {libelle}</span>}
                    {typepaiementId && <span><strong>Type :</strong> {typepaiements.find(t => t.id === typepaiementId)?.libelle || typepaiementId}</span>}
                    {projetId && <span><strong>Projet :</strong> {projets.find(p => p.id === projetId)?.libelle || projets.find(p => p.id === projetId)?.nom || projetId}</span>}
                    {description && <span><strong>Description :</strong> {description}</span>}
                  </div>
                </div>

                {/* Detected columns */}
                <div style={{ marginBottom: 16, fontSize: '0.85rem' }}>
                  <strong>Colonnes détectées :</strong>{' '}
                  {Object.entries(preview.colMap).map(([field, idx]) => (
                    <span key={field} className="badge badge-blue" style={{ margin: '2px 4px', fontSize: '0.78rem' }}>
                      {field} ({preview.headers[idx]})
                    </span>
                  ))}
                </div>

                {/* Sample data table */}
                <div style={{ marginBottom: 20 }}>
                  <strong style={{ fontSize: '0.9rem' }}>Aperçu des données ({Math.min(preview.sampleRows.length, 8)} premières lignes) :</strong>
                  <div className="table-responsive" style={{ marginTop: 8 }}>
                    <table className="data-table" style={{ fontSize: '0.82rem' }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Matricule</th>
                          {preview.colMap['prenom'] !== undefined && <th>Prénom</th>}
                          {preview.colMap['nom'] !== undefined && <th>Nom</th>}
                          <th style={{ textAlign: 'right' }}>Montant</th>
                          {preview.colMap['libelle'] !== undefined && <th>Libellé</th>}
                          {preview.colMap['annee'] !== undefined && <th>Année</th>}
                          {preview.colMap['dateversement'] !== undefined && <th>Date</th>}
                          {preview.colMap['reference'] !== undefined && <th>Référence</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sampleRows.map((row, i) => (
                          <tr key={i}>
                            <td style={{ color: '#999' }}>{i + 1}</td>
                            <td><strong>{getPreviewValue(row, 'matricule')}</strong></td>
                            {preview.colMap['prenom'] !== undefined && <td>{getPreviewValue(row, 'prenom')}</td>}
                            {preview.colMap['nom'] !== undefined && <td>{getPreviewValue(row, 'nom')}</td>}
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {preview.colMap['montant'] !== undefined
                                ? new Intl.NumberFormat('fr-FR').format(parseInt(row[preview.colMap['montant']]) || 0)
                                : '-'}
                            </td>
                            {preview.colMap['libelle'] !== undefined && <td>{getPreviewValue(row, 'libelle')}</td>}
                            {preview.colMap['annee'] !== undefined && <td>{getPreviewValue(row, 'annee')}</td>}
                            {preview.colMap['dateversement'] !== undefined && <td>{getPreviewValue(row, 'dateversement')}</td>}
                            {preview.colMap['reference'] !== undefined && <td>{getPreviewValue(row, 'reference')}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {preview.totalRows > 8 && (
                    <div style={{ textAlign: 'center', color: '#888', fontSize: '0.82rem', marginTop: 6 }}>
                      ... et {(preview.totalRows - 8).toLocaleString('fr-FR')} autres lignes
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button className="btn btn-primary" onClick={handleConfirmUpload} disabled={loading}>
                    {loading ? 'Importation en cours...' : <><FiSend /> Confirmer l'import ({preview.totalRows} lignes)</>}
                  </button>
                  <button className="btn btn-outline" onClick={() => { setStep('form'); setPreview(null); }} disabled={loading}>
                    <FiArrowLeft /> Modifier
                  </button>
                  <button className="btn btn-outline" onClick={closeForm} disabled={loading}>Annuler</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chargements history */}
      <div className="card">
        <div className="card-header">
          <span>Historique des chargements ({chargements.length})</span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Libellé</th>
                  <th>Fichier</th>
                  <th>Type</th>
                  <th>Projet</th>
                  <th style={{ textAlign: 'right' }}>Lignes</th>
                  <th style={{ textAlign: 'right' }}>Montant total</th>
                  <th>Par</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {chargements.map(c => (
                  <tr key={c.id}>
                    <td>{c.created ? new Date(c.created).toLocaleDateString('fr-FR') : '-'}</td>
                    <td><strong>{c.libelle}</strong></td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
                        <FiFile style={{ color: '#1565C0' }} /> {c.nomfichier}
                      </span>
                    </td>
                    <td>{c.type_libelle ? <span className="badge badge-blue">{c.type_libelle}</span> : '-'}</td>
                    <td>{c.projet_libelle ? <span className="badge badge-green">{c.projet_libelle}</span> : '-'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{(c.nblignes || 0).toLocaleString('fr-FR')}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(c.montant || 0)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{c.created_by_name || '-'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id, c.libelle, c.nblignes)} title="Supprimer le chargement et ses versements">
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {chargements.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Aucun chargement effectué</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chargements;
